import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from './ui/select';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { ArrowRight, Users, AlertCircle, Loader, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OVERFLOW_THRESHOLD = 5;

/**
 * Admin-only dialog for configuring a doctor's overflow chain.
 *
 * Each doctor optionally has `overflow_doctor_id`. When the doctor's waiting
 * queue reaches 5 patients, the backend automatically forwards new call
 * requests down the chain until it finds a doctor with room (or runs out
 * after 8 hops / detects a cycle).
 */
export const DoctorOverflowDialog = ({ doctor, doctors, onChanged, trigger }) => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(doctor?.overflow_doctor_id || '');
  const [saving, setSaving] = useState(false);
  const [chain, setChain] = useState([]);
  const [chainLoading, setChainLoading] = useState(false);

  // Recompute when the dialog re-opens or the doctor's saved value changes.
  useEffect(() => {
    if (open) setSelected(doctor?.overflow_doctor_id || '');
  }, [open, doctor]);

  // Build the chain (doctor → overflow → overflow's overflow ...) for display.
  useEffect(() => {
    if (!open) return;
    setChainLoading(true);
    const map = {};
    doctors.forEach((d) => { map[d._id] = d; });
    const result = [doctor];
    const visited = new Set([doctor._id]);
    let current = doctor;
    while (current?.overflow_doctor_id && !visited.has(current.overflow_doctor_id) && result.length < 8) {
      const next = map[current.overflow_doctor_id];
      if (!next) break;
      result.push(next);
      visited.add(next._id);
      current = next;
    }
    setChain(result);
    setChainLoading(false);
  }, [open, doctor, doctors]);

  const candidates = doctors.filter((d) => d._id !== doctor._id);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/doctors/${doctor._id}/overflow`,
        { overflow_doctor_id: selected || null },
        { withCredentials: true }
      );
      toast.success(t('تم تحديث الإعداد', 'Overflow updated', 'Configuration mise à jour'));
      setOpen(false);
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('فشل التحديث', 'Update failed', 'Échec'));
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/doctors/${doctor._id}/overflow`,
        { overflow_doctor_id: null },
        { withCredentials: true }
      );
      toast.success(t('تمت الإزالة', 'Cleared', 'Effacé'));
      setSelected('');
      onChanged && onChanged();
    } catch (e) {
      toast.error(t('فشل', 'Failed', 'Échec'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg" data-testid={`overflow-dialog-${doctor._id}`}>
        <DialogHeader>
          <DialogTitle className="text-[#0d47a1] flex items-center gap-2">
            <Users size={20} />
            {t('إعداد قائمة الانتظار', 'Overflow / Waiting list', 'Liste de débordement')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Explanation */}
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-sm text-gray-700 flex items-start gap-2" data-testid="overflow-help">
            <AlertCircle size={18} className="text-[#1565c0] flex-shrink-0 mt-0.5" />
            <span>
              {t(
                `عندما يصل عدد المرضى في قائمة انتظار ${doctor.full_name} إلى ${OVERFLOW_THRESHOLD}، سيتم توجيه المرضى الجدد تلقائياً إلى الطبيب التالي.`,
                `When ${doctor.full_name}'s waiting queue reaches ${OVERFLOW_THRESHOLD} patients, new patients will be redirected automatically to the next doctor.`,
                `Lorsque la file d'attente de ${doctor.full_name} atteint ${OVERFLOW_THRESHOLD} patients, les nouveaux patients seront redirigés automatiquement.`
              )}
            </span>
          </div>

          {/* Current chain visualization */}
          {!chainLoading && chain.length > 1 && (
            <div className="bg-white border rounded-xl p-3" data-testid="overflow-chain">
              <p className="text-xs uppercase font-semibold text-gray-500 mb-2">
                {t('سلسلة التوجيه الحالية', 'Current chain', 'Chaîne actuelle')}
              </p>
              <div className="flex items-center flex-wrap gap-2 text-sm">
                {chain.map((d, idx) => (
                  <React.Fragment key={d._id}>
                    <span className={`px-2.5 py-1 rounded-full font-semibold ${idx === 0 ? 'bg-[#1565c0] text-white' : 'bg-blue-100 text-[#1565c0]'}`}>
                      {d.full_name}
                    </span>
                    {idx < chain.length - 1 && <ArrowRight size={14} className="text-gray-400" />}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          {/* Picker */}
          <div>
            <label className="text-sm font-semibold text-[#0d47a1] block mb-2">
              {t('الطبيب التالي بعد امتلاء القائمة',
                'Next doctor when queue is full',
                'Médecin suivant en cas de saturation')}
            </label>
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger data-testid={`overflow-select-${doctor._id}`}>
                <SelectValue placeholder={t('لم يتم اختيار طبيب', 'No doctor selected', 'Non configuré')} />
              </SelectTrigger>
              <SelectContent>
                {candidates.map((d) => (
                  <SelectItem key={d._id} value={d._id}>
                    {d.full_name} {d.specialty ? `· ${d.specialty}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2 justify-end pt-2">
            {doctor.overflow_doctor_id && (
              <Button
                type="button"
                variant="outline"
                onClick={clear}
                disabled={saving}
                className="text-red-500 border-red-200 hover:bg-red-50"
                data-testid={`overflow-clear-${doctor._id}`}
              >
                <XCircle size={14} className="me-1" />
                {t('إزالة', 'Remove', 'Supprimer')}
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={saving}
            >
              {t('إلغاء', 'Cancel', 'Annuler')}
            </Button>
            <Button
              type="button"
              onClick={save}
              disabled={saving || !selected || selected === doctor.overflow_doctor_id}
              className="bg-[#1565c0] hover:bg-[#0d47a1]"
              data-testid={`overflow-save-${doctor._id}`}
            >
              {saving && <Loader size={14} className="me-2 animate-spin" />}
              {t('حفظ', 'Save', 'Enregistrer')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DoctorOverflowDialog;
